import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, CreditCard, Home, Car, GraduationCap, TrendingDown, DollarSign, Calendar, Percent, AlertCircle, Save, X } from 'lucide-react';
import { collection, addDoc, deleteDoc, doc, getDocs, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { useCountry } from '../CountryContext';
import { formatCurrency, getCurrencyCode } from '../countryConfig';
import { useExchangeRates } from '../hooks/useExchangeRates';
import {
  calculateTotalLiabilities,
  groupLiabilitiesByType,
  calculateMonthlyLiabilityPayments
} from '../utils/netWorthCalculations';

const LIABILITY_TYPES = [
  { value: 'Mortgage', label: 'Mortgage', icon: Home, color: '#3b82f6' },
  { value: 'Auto Loan', label: 'Auto Loan', icon: Car, color: '#10b981' },
  { value: 'Student Loan', label: 'Student Loan', icon: GraduationCap, color: '#f59e0b' },
  { value: 'Credit Card', label: 'Credit Card', icon: CreditCard, color: '#ef4444' },
  { value: 'Personal Loan', label: 'Personal Loan', icon: DollarSign, color: '#8b5cf6' },
  { value: 'Other', label: 'Other', icon: TrendingDown, color: '#6b7280' }
];

const LiabilitiesManager = ({ isDark }) => {
  const { currentUser } = useAuth();
  const { selectedCountry } = useCountry();
  const { rates: exchangeRates } = useExchangeRates(); // ⭐ MULTI-CURRENCY: Get exchange rates
  const [liabilities, setLiabilities] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLiability, setEditingLiability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'Credit Card',
    currentBalance: '',
    originalAmount: '',
    interestRate: '',
    monthlyPayment: '',
    startDate: '',
    notes: '',
    currency: getCurrencyCode(selectedCountry) // ⭐ NEW: Add currency field
  });

  // Load liabilities from Firestore
  useEffect(() => {
    if (currentUser) {
      loadLiabilities();
    }
  }, [currentUser]);

  const loadLiabilities = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'users', currentUser.uid, 'liabilities')
      );
      const querySnapshot = await getDocs(q);
      const loadedLiabilities = [];

      querySnapshot.forEach((doc) => {
        loadedLiabilities.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setLiabilities(loadedLiabilities);
    } catch (error) {
      console.error('Error loading liabilities:', error);
      alert('Failed to load liabilities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'Credit Card',
      currentBalance: '',
      originalAmount: '',
      interestRate: '',
      monthlyPayment: '',
      startDate: '',
      notes: '',
      currency: getCurrencyCode(selectedCountry) // ⭐ NEW: Reset currency to current country
    });
  };

  const handleAddLiability = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.currentBalance) {
      alert('Please fill in required fields (name and current balance)');
      return;
    }

    setSaving(true);
    try {
      const liabilityData = {
        ...formData,
        currentBalance: parseFloat(formData.currentBalance) || 0,
        originalAmount: parseFloat(formData.originalAmount) || 0,
        interestRate: parseFloat(formData.interestRate) || 0,
        monthlyPayment: parseFloat(formData.monthlyPayment) || 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(
        collection(db, 'users', currentUser.uid, 'liabilities'),
        liabilityData
      );

      await loadLiabilities();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Error adding liability:', error);
      alert('Failed to add liability. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditLiability = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.currentBalance) {
      alert('Please fill in required fields (name and current balance)');
      return;
    }

    setSaving(true);
    try {
      const liabilityData = {
        ...formData,
        currentBalance: parseFloat(formData.currentBalance) || 0,
        originalAmount: parseFloat(formData.originalAmount) || 0,
        interestRate: parseFloat(formData.interestRate) || 0,
        monthlyPayment: parseFloat(formData.monthlyPayment) || 0,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(
        doc(db, 'users', currentUser.uid, 'liabilities', editingLiability.id),
        liabilityData
      );

      await loadLiabilities();
      setShowEditModal(false);
      setEditingLiability(null);
      resetForm();
    } catch (error) {
      console.error('Error updating liability:', error);
      alert('Failed to update liability. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLiability = async (liabilityId) => {
    if (!window.confirm('Are you sure you want to delete this liability?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'users', currentUser.uid, 'liabilities', liabilityId));
      await loadLiabilities();
    } catch (error) {
      console.error('Error deleting liability:', error);
      alert('Failed to delete liability. Please try again.');
    }
  };

  const openEditModal = (liability) => {
    setEditingLiability(liability);
    setFormData({
      name: liability.name,
      type: liability.type,
      currentBalance: liability.currentBalance.toString(),
      originalAmount: liability.originalAmount?.toString() || '',
      interestRate: liability.interestRate?.toString() || '',
      monthlyPayment: liability.monthlyPayment?.toString() || '',
      startDate: liability.startDate || '',
      notes: liability.notes || '',
      currency: liability.currency || getCurrencyCode(selectedCountry) // ⭐ NEW: Load currency from liability
    });
    setShowEditModal(true);
  };

  // ⭐ MULTI-CURRENCY: Pass display currency and exchange rates for conversion
  const displayCurrency = getCurrencyCode(selectedCountry);
  const totalLiabilities = calculateTotalLiabilities(liabilities, displayCurrency, exchangeRates);
  const monthlyPayments = calculateMonthlyLiabilityPayments(liabilities, displayCurrency, exchangeRates);
  const groupedLiabilities = groupLiabilitiesByType(liabilities, displayCurrency, exchangeRates);

  const getTypeIcon = (type) => {
    const typeConfig = LIABILITY_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.icon : TrendingDown;
  };

  const getTypeColor = (type) => {
    const typeConfig = LIABILITY_TYPES.find(t => t.value === type);
    return typeConfig ? typeConfig.color : '#6b7280';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Header and Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Liabilities
              </p>
              <p className="text-2xl font-bold text-red-500 mt-1">
                {formatCurrency(totalLiabilities, selectedCountry)}
              </p>
            </div>
            <TrendingDown className="w-12 h-12 text-red-500 opacity-20" />
          </div>
        </div>

        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Monthly Payments
              </p>
              <p className="text-2xl font-bold text-orange-500 mt-1">
                {formatCurrency(monthlyPayments, selectedCountry)}
              </p>
            </div>
            <Calendar className="w-12 h-12 text-orange-500 opacity-20" />
          </div>
        </div>

        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Liabilities
              </p>
              <p className="text-2xl font-bold mt-1">
                {liabilities.length}
              </p>
            </div>
            <CreditCard className="w-12 h-12 text-blue-500 opacity-20" />
          </div>
        </div>
      </div>

      {/* Add Liability Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Your Liabilities</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Liability
        </button>
      </div>

      {/* Liabilities List */}
      {liabilities.length === 0 ? (
        <div className={`text-center py-12 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <AlertCircle className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No liabilities added yet
          </p>
          <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-2`}>
            Start tracking your debts to calculate your net worth
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {liabilities.map((liability) => {
            const Icon = getTypeIcon(liability.type);
            const color = getTypeColor(liability.type);
            const percentPaid = liability.originalAmount > 0
              ? ((liability.originalAmount - liability.currentBalance) / liability.originalAmount) * 100
              : 0;

            return (
              <div
                key={liability.id}
                className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg hover:shadow-xl transition-shadow`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color }} />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-lg font-semibold">{liability.name}</h3>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {liability.type}
                      </p>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Current Balance
                          </p>
                          <p className="text-lg font-bold text-red-500">
                            {formatCurrency(liability.currentBalance, selectedCountry)}
                          </p>
                        </div>

                        {liability.monthlyPayment > 0 && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Monthly Payment
                            </p>
                            <p className="text-lg font-semibold">
                              {formatCurrency(liability.monthlyPayment, selectedCountry)}
                            </p>
                          </div>
                        )}

                        {liability.interestRate > 0 && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Interest Rate
                            </p>
                            <p className="text-lg font-semibold">
                              {liability.interestRate}%
                            </p>
                          </div>
                        )}

                        {liability.originalAmount > 0 && (
                          <div>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                              Original Amount
                            </p>
                            <p className="text-lg font-semibold">
                              {formatCurrency(liability.originalAmount, selectedCountry)}
                            </p>
                          </div>
                        )}
                      </div>

                      {liability.originalAmount > 0 && (
                        <div className="mt-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              Paid Off
                            </span>
                            <span className="font-semibold">
                              {percentPaid.toFixed(1)}%
                            </span>
                          </div>
                          <div className={`w-full h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <div
                              className="h-2 rounded-full bg-green-500"
                              style={{ width: `${Math.min(percentPaid, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {liability.notes && (
                        <p className={`text-sm mt-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {liability.notes}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => openEditModal(liability)}
                      className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteLiability(liability.id)}
                      className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`max-w-2xl w-full rounded-lg p-6 ${isDark ? 'bg-gray-800' : 'bg-white'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {showEditModal ? 'Edit Liability' : 'Add New Liability'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setEditingLiability(null);
                  resetForm();
                }}
                className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={showEditModal ? handleEditLiability : handleAddLiability} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Chase Credit Card"
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  required
                >
                  {LIABILITY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* ⭐ MULTI-CURRENCY: Currency selector */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Currency
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="USD">🇺🇸 USD - US Dollar</option>
                  <option value="GBP">🇬🇧 GBP - British Pound</option>
                  <option value="EUR">🇪🇺 EUR - Euro</option>
                </select>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Select the currency this liability is denominated in
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Current Balance ({formData.currency}) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="currentBalance"
                    value={formData.currentBalance}
                    onChange={handleInputChange}
                    placeholder="10000"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    required
                  />
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Enter the balance in {formData.currency}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Original Amount
                  </label>
                  <input
                    type="number"
                    name="originalAmount"
                    value={formData.originalAmount}
                    onChange={handleInputChange}
                    placeholder="15000"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Interest Rate (%)
                  </label>
                  <input
                    type="number"
                    name="interestRate"
                    value={formData.interestRate}
                    onChange={handleInputChange}
                    placeholder="5.5"
                    step="0.01"
                    min="0"
                    max="100"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Monthly Payment ({formData.currency})
                  </label>
                  <input
                    type="number"
                    name="monthlyPayment"
                    value={formData.monthlyPayment}
                    onChange={handleInputChange}
                    placeholder="300"
                    step="0.01"
                    min="0"
                    className={`w-full p-3 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Notes
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Additional notes..."
                  rows="3"
                  className={`w-full p-3 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {showEditModal ? 'Update Liability' : 'Add Liability'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setEditingLiability(null);
                    resetForm();
                  }}
                  className={`px-6 py-3 rounded-lg ${
                    isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                  } transition-colors`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiabilitiesManager;
